'use client'

import { Copy, Download, EyeIcon, EyeOffIcon, LinkIcon, Loader2, Share2 } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { OrderXLogoIcon } from '@/components/icons'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import QRCode from '@/components/widget/qrcode'
import { useNumberFormat } from '@/hooks/use-number-format'
import { copyText } from '@/lib/clipboard'
import { SOCIAL_CONFIG } from '@/lib/configs/social-config'

const JOIN_URL = 'https://app.orderx.com/join'

export interface SavingsCardData {
  days: number
  savedUsd: number
  volumeUsd: number
  makerShare: number | null
  fillCount: number
}

/**
 * The "Saved with OrderX" card to post (Alex: shareable, like a PnL card): taker fees saved
 * over the window, the volume behind it and the maker share, with the join link and QR code.
 * Amounts can be hidden so the card shows only the rate (bps of volume) — the default while
 * the app's hide-balances toggle is on. Same image flow as the positions PnL card.
 */
export function HomeShareSavingsDialog({
  data,
  open,
  onOpenChange,
}: {
  data: SavingsCardData
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const t = useTranslations('trade')
  const ts = useTranslations('trade.home.trading.share')
  const locale = useLocale()
  const fmt = useNumberFormat()
  const cardRef = useRef<HTMLDivElement>(null)
  const [busy, setBusy] = useState(false)
  const [canCopyImage, setCanCopyImage] = useState(false)
  const [showAmounts, setShowAmounts] = useState(!fmt.hidden)

  useEffect(() => {
    setCanCopyImage(
      window.isSecureContext &&
        typeof navigator.clipboard?.write === 'function' &&
        typeof window.ClipboardItem === 'function'
    )
  }, [])
  useEffect(() => {
    if (open) setShowAmounts(!fmt.hidden)
  }, [open, fmt.hidden])

  // Locale currency formatting on the card itself: it's an image for others, not the app's
  // number-format setting (same as the PnL card).
  const usd = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: data.savedUsd >= 100 ? 0 : 2,
  })
  const compactUsd = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  })
  const bps = data.volumeUsd > 0 ? (data.savedUsd / data.volumeUsd) * 10_000 : 0
  const bpsText = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(bps)

  const render = async () => {
    if (!cardRef.current) throw new Error('Savings card is not available')
    const { toBlob } = await import('html-to-image')
    const blob = await toBlob(cardRef.current, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: '#001012',
    })
    if (!blob) throw new Error('Failed to render the savings card')
    return blob
  }

  const imageAction = async (action: 'copy' | 'download') => {
    if (busy) return
    setBusy(true)
    try {
      const blob = await render()
      if (action === 'copy') {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
        toast.success(t('sharePnlImageCopied'))
      } else {
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = `orderx-savings-${new Date().toISOString().slice(0, 10)}.png`
        anchor.click()
        URL.revokeObjectURL(url)
        toast.success(t('sharePnlImageDownloaded'))
      }
    } catch {
      toast.error(
        action === 'copy' ? t('sharePnlImageCopyFailed') : t('sharePnlImageDownloadFailed')
      )
    } finally {
      setBusy(false)
    }
  }

  const copyLink = async () => {
    if (await copyText(JOIN_URL)) toast.success(t('sharePnlLinkCopied'))
    else toast.error(t('sharePnlLinkCopyFailed'))
  }

  const shareOnX = () => {
    let handle = 'orderx'
    try {
      handle = new URL(SOCIAL_CONFIG.twitter).pathname.replace(/^\/+|\/+$/g, '') || handle
    } catch {
      // Keep the fallback handle if the configured social URL is malformed.
    }
    const params = new URLSearchParams({
      text: showAmounts
        ? ts('xText', { saved: usd.format(data.savedUsd), days: data.days })
        : ts('xTextRate', { bps: bpsText, days: data.days }),
      url: JOIN_URL,
      via: handle,
    })
    const width = 600
    const height = 700
    const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2)
    const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2)
    window.open(
      `https://twitter.com/intent/tweet?${params.toString()}`,
      'orderx-x-share',
      `popup=yes,width=${width},height=${height},left=${Math.round(left)},top=${Math.round(top)},noopener,noreferrer`
    )
  }

  const secondary =
    'flex h-11 items-center justify-center gap-2 border border-[var(--share-pnl-secondary-border)] bg-[var(--share-pnl-secondary)] text-sm font-semibold text-[var(--share-pnl-secondary-foreground)] shadow-sm transition-[background-color,transform] duration-150 ease-out hover:bg-[var(--share-pnl-secondary-hover)] active:scale-[0.97] disabled:opacity-50'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-24px)] min-h-0 w-[min(540px,calc(100vw-24px))] max-w-none gap-4 overflow-y-auto border-[var(--share-pnl-border)] bg-[image:var(--share-pnl-surface)] p-4 text-[var(--share-pnl-foreground)] sm:p-6 [&>button.absolute]:top-5 [&>button.absolute]:right-5 [&>button.absolute]:rounded-full [&>button.absolute]:p-1.5 [&>button.absolute]:text-[var(--share-pnl-muted)] sm:[&>button.absolute]:top-6 sm:[&>button.absolute]:right-6">
        <div className="flex items-center gap-3 px-2 pr-10">
          <DialogTitle className="text-base font-semibold tracking-[-0.01em]">
            {ts('title')}
          </DialogTitle>
          <button
            type="button"
            onClick={() => setShowAmounts((value) => !value)}
            className="ml-auto flex items-center gap-1.5 text-xs text-[var(--share-pnl-muted)] transition-colors hover:text-[var(--share-pnl-foreground)]"
          >
            {showAmounts ? <EyeOffIcon className="size-3.5" /> : <EyeIcon className="size-3.5" />}
            {showAmounts ? ts('hideAmounts') : ts('showAmounts')}
          </button>
        </div>

        {/* The image: dark on purpose (it's posted, not themed), same palette as the PnL card. */}
        <div
          ref={cardRef}
          className="border-ox-paper/10 text-ox-paper flex flex-col gap-6 overflow-hidden border bg-[radial-gradient(circle_at_85%_0%,rgba(52,156,183,0.5)_0%,rgba(7,90,118,0.28)_34%,rgba(6,21,32,0.12)_100%),#001012] p-5 sm:p-6"
        >
          <div className="flex items-center justify-between gap-3">
            <span className="bg-ox-cyan/20 text-ox-cyan px-2 py-1 text-xs font-medium">
              {ts('badge', { days: data.days })}
            </span>
            <OrderXLogoIcon
              className="!fill-ox-paper text-ox-paper h-3.5 w-auto"
              aria-label="OrderX"
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-cat-sky text-sm font-semibold">{ts('headline')}</span>
            <span className="text-ox-cyan text-5xl leading-none tracking-[-0.05em] sm:text-6xl">
              {showAmounts ? usd.format(data.savedUsd) : `${bpsText} bps`}
            </span>
            <span className="text-ox-paper/70 text-sm">
              {showAmounts ? ts('sub', { bps: bpsText }) : ts('subRate')}
            </span>
          </div>

          <div className="bg-cat-sky/10 grid grid-cols-3 gap-3 p-4">
            <div className="flex flex-col gap-1">
              <span className="text-cat-sky text-xs font-semibold">{ts('volume')}</span>
              <span className="text-lg font-medium">
                {showAmounts ? compactUsd.format(data.volumeUsd) : '—'}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-cat-sky text-xs font-semibold">{ts('makerShare')}</span>
              <span className="text-lg font-medium">
                {data.makerShare === null ? '—' : `${Math.round(data.makerShare * 100)}%`}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-cat-sky text-xs font-semibold">{ts('fills')}</span>
              <span className="text-lg font-medium">
                {new Intl.NumberFormat(locale).format(data.fillCount)}
              </span>
            </div>
          </div>

          <div className="flex items-end justify-between gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-cat-sky text-xs font-semibold">{t('sharePnlJoinOrderX')}</span>
              <span className="text-ox-paper text-sm font-medium break-all">{JOIN_URL}</span>
            </div>
            <QRCode secret={JOIN_URL} size={80} className="shrink-0 p-1.5" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 p-2 sm:grid-cols-3">
          <button type="button" className={secondary} onClick={copyLink}>
            <LinkIcon className="size-4" />
            {t('sharePnlCopyLink')}
          </button>
          <button
            type="button"
            disabled={busy}
            className={secondary}
            onClick={() => imageAction(canCopyImage ? 'copy' : 'download')}
          >
            {busy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : canCopyImage ? (
              <Copy className="size-4" />
            ) : (
              <Download className="size-4" />
            )}
            {canCopyImage ? t('sharePnlCopyImage') : t('sharePnlDownloadImage')}
          </button>
          <button
            type="button"
            className="flex h-11 items-center justify-center gap-2 bg-[var(--share-pnl-primary)] text-sm font-semibold text-[var(--share-pnl-primary-foreground)] transition-[background-color,transform] duration-150 ease-out hover:bg-[var(--share-pnl-primary-hover)] active:scale-[0.97]"
            onClick={shareOnX}
          >
            <Share2 className="size-4" />
            {t('sharePnlShare')}
          </button>
        </div>
        {canCopyImage && (
          <button
            type="button"
            disabled={busy}
            onClick={() => imageAction('download')}
            className="mx-auto -mt-2 flex items-center gap-1.5 text-xs text-[var(--share-pnl-muted)] transition-colors hover:text-[var(--share-pnl-foreground)]"
          >
            <Download className="size-3.5" />
            {t('sharePnlDownloadImage')}
          </button>
        )}
      </DialogContent>
    </Dialog>
  )
}
